import { useMemo } from "react";
import type { ShoppingCartDto } from "@lib/ShiOneClient";
import useGetShoppingCart from "@features/unified-user-header-feature/hooks/useGetShoppingCart";
import useUpdateShoppingCartItemQty from "@features/unified-user-header-feature/hooks/useUpdateShoppingCartItemQty";
import useClearCart from "@features/unified-user-header-feature/hooks/useClearCart";
import { showProductRemovedToast } from "./cartToasts";

export interface UseCartResult {
  items: ShoppingCartDto[];
  /** Sum of line quantities. */
  itemCount: number;
  /** Sum of unitPrice * quantity (the cart API exposes unit prices only). */
  subtotal: number;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  updateQuantity: (args: { lineNumber: number; quantity: number }) => void;
  removeItem: (lineNumber: number) => void;
  clearCart: () => void;
  isMutating: boolean;
}

/**
 * Canonical shared cart data hook. Both the mini-cart dropdown and the full cart
 * page consume this so they operate on one React Query cache entry and stay in
 * sync. Item count and subtotal are derived client-side because the cart API
 * exposes unit prices only (no line/cart totals), which also keeps the cart
 * within the epic's pricing rules (subtotal only; no tax/shipping/total).
 */
export function useCart(): UseCartResult {
  const query = useGetShoppingCart();
  const updateQty = useUpdateShoppingCartItemQty();
  const clear = useClearCart();

  const items = useMemo<ShoppingCartDto[]>(() => query.data ?? [], [query.data]);

  const itemCount = useMemo(
    () => items.reduce((sum, i) => sum + (i.quantity ?? 0), 0),
    [items],
  );

  const subtotal = useMemo(
    () =>
      items.reduce((sum, i) => sum + (i.unitPrice ?? 0) * (i.quantity ?? 0), 0),
    [items],
  );

  return {
    items,
    itemCount,
    subtotal,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => void query.refetch(),
    updateQuantity: (args) => updateQty.mutate(args),
    removeItem: (lineNumber) => {
      // Guard against non-positive line numbers (e.g. a missing lineNumber coerced to 0)
      // so we never fire a delete request for an invalid line.
      if (lineNumber > 0) {
        const removed = items.find((i) => i.lineNumber === lineNumber);
        clear.mutate([lineNumber], {
          onSuccess: () => showProductRemovedToast(removed?.productName),
        });
      }
    },
    clearCart: () =>
      clear.mutate(items.map((i) => i.lineNumber ?? 0).filter((n) => n > 0)),
    isMutating: updateQty.isLoading || clear.isLoading,
  };
}

export default useCart;
